import { Moon, Settings, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import darkLockup from "~/../public/lockup_dark.svg";
import lightLockup from "~/../public/lockup_transparent.svg";
import { api } from "~/api";
import { useAuth } from "~/contexts/auth";
import { ModalProvider } from "~/contexts/modal";
import { ApiForm } from "~/routes/_index/components/ApiForm";
import { AttachOrgToCustomerForm } from "~/routes/_index/components/AttachOrgToCustomerForm";
import { AttachSlackChannelForm } from "~/routes/_index/components/AttachSlackChannelForm";
import { ByocKitForm } from "~/routes/_index/components/ByocKitForm";
import { CustomerSearchModal } from "~/routes/_index/components/CustomerSearchModal";
import { NamespaceSearchModal } from "~/routes/_index/components/NamespaceSearchModal";
import { OrgSearchModal } from "~/routes/_index/components/OrgSearchModal";
import { RegisterClusterForm } from "~/routes/_index/components/RegisterClusterForm";
import { StoragePrefixForm } from "~/routes/_index/components/StoragePrefixForm";

import { Button } from "./Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { OperationModal } from "./OperationModal";

const REVISION = api.getRevision();

export type ModalType =
  | "api"
  | "dashboard"
  | "slack"
  | "byoc-kit"
  | "register-cluster"
  | "org-search"
  | "customer-search"
  | "namespace-search"
  | "storage-prefix";

export interface ModalConfig {
  title: string;
  description: string;
  component: React.ComponentType<any>;
  className?: string;
  closeOnSuccess?: boolean;
}

export const MODAL_CONFIGS: Record<ModalType, ModalConfig> = {
  api: {
    title: "Create Customer API Key",
    description: "Generate a new API key for a customer",
    component: ApiForm,
    className: "sm:max-w-md max-h-[90vh] overflow-y-auto",
    closeOnSuccess: true,
  },
  dashboard: {
    title: "Attach Org to Customer",
    description: "Link an existing BYOC org to its dashboard customer",
    component: AttachOrgToCustomerForm,
    className: "sm:max-w-md max-h-[90vh] overflow-y-auto",
  },
  slack: {
    title: "Attach Slack Channel",
    description:
      "Attach a Slack channel to a customer for approval notifications. This is an upsert operation",
    component: AttachSlackChannelForm,
    className: "sm:max-w-md",
    closeOnSuccess: true,
  },
  "byoc-kit": {
    title: "Generate BYOC Kit",
    description: "",
    component: ByocKitForm,
    className: "sm:max-w-4xl max-h-[90vh] overflow-y-auto",
  },
  "register-cluster": {
    title: "Register Cluster",
    description:
      "Register a cluster by hand when BYOC auto-registration failed. Uses the same register-cluster path as automatic registration.",
    component: RegisterClusterForm,
    className: "sm:max-w-md",
    closeOnSuccess: true,
  },
  "org-search": {
    title: "Search Organizations",
    description: "Find and navigate to an organization",
    component: OrgSearchModal,
    className: "sm:max-w-md",
    closeOnSuccess: true,
  },
  "customer-search": {
    title: "Search Customers",
    description: "Find and navigate to a customer",
    component: CustomerSearchModal,
    className: "sm:max-w-md",
    closeOnSuccess: true,
  },
  "namespace-search": {
    title: "Search Namespaces",
    description: "Find and navigate to a namespace",
    component: NamespaceSearchModal,
    className: "sm:max-w-md",
    closeOnSuccess: true,
  },
  "storage-prefix": {
    title: "Compute Storage Prefix",
    description:
      "XXH3 hash prefix used as the object-storage path for an org + namespace",
    component: StoragePrefixForm,
    className: "sm:max-w-lg",
  },
};

export default function MainLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { status } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const isBillingMigration = pathname === "/billing-migration";
  const pageLabel = pathname.startsWith("/orgs/")
    ? "orgs"
    : pathname.startsWith("/namespaces/")
      ? "namespaces"
      : pathname.startsWith("/customers/")
        ? "customers"
        : null;
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [revision, setRevision] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    REVISION.then(setRevision);
  }, []);

  useEffect(() => {
    const colorScheme = window.matchMedia("(prefers-color-scheme: dark)");
    const followSystemTheme = (event: MediaQueryListEvent) => {
      if (localStorage.getItem("theme") === null) {
        document.documentElement.classList.toggle("dark", event.matches);
        setIsDarkMode(event.matches);
      }
    };

    colorScheme.addEventListener("change", followSystemTheme);
    return () => colorScheme.removeEventListener("change", followSystemTheme);
  }, []);

  // Check if any modal is open
  const isAnyModalOpen = showSettingsMenu || activeModal !== null;

  // Function to open a specific modal
  const openModal = (modalType: ModalType) => {
    setActiveModal(modalType);
  };

  const toggleDarkMode = () => {
    const nextIsDarkMode = !isDarkMode;
    document.documentElement.classList.toggle("dark", nextIsDarkMode);
    localStorage.setItem("theme", nextIsDarkMode ? "dark" : "light");
    setIsDarkMode(nextIsDarkMode);
  };

  return (
    <ModalProvider isAnyModalOpen={isAnyModalOpen} openModal={openModal}>
      <div
        className={`${isBillingMigration ? "w-full max-w-none" : "container"} flex flex-col items-center justify-center gap-8 px-2 py-6 sm:px-4 sm:py-12 md:px-8`}
      >
        <div
          className={`${isBillingMigration ? "max-w-none" : "md:max-w-4xl"} flex w-full flex-col gap-4`}
        >
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <Link to="/">
                <img
                  src={isDarkMode ? darkLockup : lightLockup}
                  alt="turbopuffer"
                  width="200"
                  className="my-2 sm:my-4"
                />
              </Link>
              {pageLabel ? (
                <>
                  <span className="text-gray-300">/</span>
                  <span className="text-sm font-medium text-gray-400">
                    {pageLabel}
                  </span>
                </>
              ) : null}
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              {status === "authorized" ? (
                <>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="flex flex-1 items-center justify-center sm:flex-none"
                  >
                    <Link to="/fleet-ops">Fleet Ops</Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="flex flex-1 items-center justify-center sm:flex-none"
                  >
                    <Link to="/settings-overrides">Overrides</Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="flex flex-1 items-center justify-center sm:flex-none"
                  >
                    <Link to="/orgs-purge">Orgs to Purge</Link>
                  </Button>
                  <Button
                    onClick={() => setShowSettingsMenu(true)}
                    variant="outline"
                    size="sm"
                    className="flex flex-1 items-center justify-center gap-2 sm:flex-none"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      Customer Operations
                    </span>
                    <span className="sm:hidden">Operations</span>
                  </Button>
                  <Button variant="outline" size="sm" className="flex-shrink-0">
                    <a href="/auth/logout">Logout</a>
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <a href="/auth/google">Login with Google</a>
                </Button>
              )}
            </div>
          </div>
          {children}
        </div>

        {/* Settings Menu Modal */}
        <Dialog open={showSettingsMenu} onOpenChange={setShowSettingsMenu}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Customer Operations</DialogTitle>
              <DialogDescription>
                Select an operation to perform
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              {Object.entries(MODAL_CONFIGS).map(([key, config]) => (
                <Button
                  key={key}
                  onClick={() => {
                    setShowSettingsMenu(false);
                    setActiveModal(key as ModalType);
                  }}
                  className="w-full justify-start"
                  variant="outline"
                >
                  {config.title}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Individual Operation Modals */}
        {activeModal
          ? (() => {
              const config = MODAL_CONFIGS[activeModal];
              const Component = config.component;
              const componentProps = config.closeOnSuccess
                ? { onSuccess: () => setActiveModal(null) }
                : {};

              return (
                <OperationModal
                  isOpen={true}
                  onClose={() => setActiveModal(null)}
                  title={config.title}
                  description={config.description}
                  className={config.className}
                >
                  <Component {...componentProps} />
                </OperationModal>
              );
            })()
          : null}
        <div className="flex items-center gap-2 text-muted-foreground">
          <p className="text-xs">
            <a
              className="hover:underline"
              href="https://github.com/turbopuffer/tpuf-ctl-vendor"
            >
              tpuf-ctl-vendor
            </a>
            {revision ? ` ${revision}` : null}
          </p>
          <Button
            aria-label={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
            title={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
            onClick={toggleDarkMode}
            variant="ghost"
            size="icon"
          >
            {isDarkMode ? (
              <Sun className="h-3.5 w-3.5" />
            ) : (
              <Moon className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
    </ModalProvider>
  );
}
